import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BookOpen, Heart, GraduationCap, Youtube, Sparkles, Mail, Quote } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";
import { LOGO_URL, MINISTRY, SUBSCRIBE_BADGE } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const decodeEntities = (s = "") =>
  s.replace(/&#8217;/g, "’").replace(/&#8216;/g, "‘").replace(/&#038;/g, "&").replace(/&amp;/g, "&")
    .replace(/&#8211;/g, "–").replace(/&#8212;/g, "—").replace(/&quot;/g, "\"").replace(/&#39;/g, "'");

const QUICK_LINKS = [
  { to: "/prayer-requests", title: "Prayer Requests", desc: "Bring your burdens before the Lord.", Icon: Heart },
  { to: "/bible-studies", title: "Bible Studies", desc: "Daniel, Revelation, prophecy and more.", Icon: GraduationCap },
  { to: "/bible-promises", title: "Bible Promises", desc: "God's Word for every need of the heart.", Icon: Sparkles },
  { to: "/videos", title: "Watch Videos", desc: "Christ-centered messages on YouTube.", Icon: Youtube },
];

export default function Home() {
  const [devotional, setDevotional] = useState(null);
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState("");

  useEffect(() => {
    axios.get(`${API}/devotionals`).then((r) => setDevotional(r.data?.[0])).catch(() => {});
  }, []);

  const onSubscribe = async (e) => {
    e.preventDefault();
    setSubStatus("");
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubStatus("Welcome! You are subscribed.");
      setEmail("");
    } catch {
      setSubStatus("Please enter a valid email.");
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "var(--navy-900)" }}>
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 70% 30%, rgba(217,184,100,0.55) 0%, transparent 45%), radial-gradient(circle at 20% 80%, rgba(36,78,120,0.7) 0%, transparent 50%)",
          }}
        />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22><path d=%22M30 8 v44 M8 30 h44%22 stroke=%22%23E6CB89%22 stroke-width=%221%22/></svg>')" }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-cream reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="text-gold-light text-xs uppercase tracking-[0.32em]">Abide Daily Ministry</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] text-cream">
              Helping you <span className="text-gold-light italic">abide</span>
              <br /> in Christ every day.
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-cream/85 leading-relaxed max-w-xl">
              Daily Scripture. Honest prayer. Faithful Bible study. A quiet place to walk with Jesus —
              one verse, one prayer, one breath at a time.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/devotionals"
                data-testid="hero-cta-devotional"
                className="btn-gold px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2 text-base"
              >
                Read Today's Devotional <ArrowRight size={18} />
              </Link>
              <Link
                to="/prayer-requests"
                data-testid="hero-cta-prayer"
                className="px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2 border border-cream/40 text-cream hover:bg-cream hover:text-navy-deep transition-all text-base"
              >
                <Heart size={18} /> Submit a Prayer Request
              </Link>
            </div>
          </div>

          {/* Hero logo emblem */}
          <div className="relative reveal reveal-delay-2 hidden lg:block">
            <div
              className="absolute -inset-6 rounded-full blur-3xl opacity-60"
              style={{ background: "radial-gradient(circle, rgba(217,184,100,0.45), transparent 70%)" }}
            />
            <div className="relative aspect-square max-w-md mx-auto">
              <div className="w-full h-full flex items-center justify-center drop-shadow-2xl">
                <img src={LOGO_URL} alt="Abide Daily emblem" className="w-full h-full object-contain" />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 rounded-full bg-gold text-navy-deep text-xs uppercase tracking-[0.3em] font-semibold shadow-lg">
                John 15:5
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION STATEMENT */}
      <section className="py-20 lg:py-28 paper-texture">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center reveal">
          <div className="ornament mb-6">
            <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">Our Mission</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl text-navy-deep leading-tight">
            To help every weary heart pause, listen, and learn the quiet rhythm of abiding in Jesus.
          </h2>
          <p className="mt-7 text-lg text-navy/70 leading-relaxed">
            We believe Christ is the center of every devotional, every study, and every prayer. Abide Daily exists to
            point you back to Him — through Scripture you can trust, prayers that reach the throne, and encouragement
            for the road home.
          </p>
        </div>
      </section>

      {/* FEATURED DEVOTIONAL */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: "var(--cream-100)" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2 reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-medium mb-3">
              {devotional?.is_featured ? "Featured Devotional" : "Today's Devotional"}
            </div>
            <h3 className="font-display text-3xl lg:text-4xl text-navy-deep leading-tight">
              {decodeEntities(devotional?.title || "Abide in the Vine")}
            </h3>
            <p className="mt-3 text-sm text-navy/60 italic">
              {devotional?.published_at ? new Date(devotional.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}
              {devotional?.category_names?.[0] && <> · {decodeEntities(devotional.category_names[0])}</>}
            </p>
          </div>
          <article className="lg:col-span-3 bg-white rounded-2xl overflow-hidden shadow-[0_20px_60px_-30px_rgba(11,31,58,0.35)] border border-cream-200 reveal reveal-delay-1">
            {devotional?.featured_image_url && (
              <img src={devotional.featured_image_url} alt={decodeEntities(devotional.title)} className="w-full h-56 object-cover" />
            )}
            <div className="p-8 lg:p-10">
              <Quote className="text-gold mb-3" size={28} />
              <p className="text-navy/85 text-lg leading-relaxed">
                {decodeEntities(devotional?.excerpt || "When we remain in Christ, His life flows through us like sap through a branch.")}
              </p>
              <Link
                to={devotional?.slug ? `/devotionals/${devotional.slug}` : "/devotionals"}
                data-testid="featured-devotional-link"
                className="mt-7 inline-flex items-center gap-2 btn-gold px-6 py-3 rounded-full text-sm"
              >
                Read the Devotional <ArrowRight size={16} />
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* QUICK ACCESS CARDS */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="ornament mb-4">
              <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">Begin Anywhere</span>
            </div>
            <h2 className="font-display text-3xl lg:text-5xl text-navy-deep">Where will you abide today?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {QUICK_LINKS.map(({ to, title, desc, Icon }, i) => (
              <Link
                key={to}
                to={to}
                data-testid={`quick-link-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`lift bg-white rounded-2xl p-6 border border-cream-200 hover:border-gold relative overflow-hidden group reveal reveal-delay-${(i % 3) + 1}`}
              >
                <div
                  className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10 transition-opacity group-hover:opacity-25"
                  style={{ backgroundColor: "var(--gold-500)" }}
                />
                <div
                  className="relative w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}
                >
                  <Icon size={22} />
                </div>
                <h4 className="font-display text-xl text-navy-deep mb-2">{title}</h4>
                <p className="text-sm text-navy/65 leading-relaxed">{desc}</p>
                <div className="mt-4 text-gold text-sm font-semibold inline-flex items-center gap-1.5">
                  Explore <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED VIDEO */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: "var(--navy-900)" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-cream">
            <div className="text-xs uppercase tracking-[0.3em] text-gold-light font-medium mb-3">Featured Video</div>
            <h2 className="font-display text-3xl lg:text-5xl leading-tight text-cream">
              Sit with Jesus. <span className="italic text-gold-light">Listen.</span>
            </h2>
            <p className="mt-5 text-cream/80 leading-relaxed text-lg max-w-md">
              Fresh devotionals, prophecy studies, and Christ-centered messages on our YouTube channel.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-4">
              <a
                href={MINISTRY.youtube}
                target="_blank"
                rel="noreferrer"
                data-testid="featured-video-subscribe"
                className="btn-gold px-6 py-3 rounded-full inline-flex items-center justify-center gap-2"
              >
                <Youtube size={18} /> Subscribe on YouTube
              </a>
              <Link
                to="/videos"
                className="px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 border border-cream/40 text-cream hover:bg-cream hover:text-navy-deep transition-all"
              >
                Watch More Videos
              </Link>
            </div>
          </div>
          <div className="relative aspect-video rounded-2xl overflow-hidden border-4 border-gold/40 shadow-2xl group">
            <a
              href={MINISTRY.youtube}
              target="_blank"
              rel="noreferrer"
              data-testid="home-featured-video"
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, var(--navy-800) 0%, var(--navy-600) 50%, var(--gold-500) 100%)",
              }}
            >
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), transparent 60%)" }} />
              <div className="relative text-center text-cream px-8">
                <div className="w-20 h-20 mx-auto rounded-full bg-cream/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform mb-4">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="var(--navy-900)" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <p className="font-display text-2xl text-cream">Watch on YouTube</p>
                <p className="mt-2 text-gold-light text-sm tracking-[0.25em] uppercase">{MINISTRY.youtubeHandle}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* SUBSCRIBE CTA */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: "var(--cream-100)" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl bg-white p-8 lg:p-14 grid lg:grid-cols-5 gap-8 items-center border border-cream-200 shadow-[0_30px_80px_-40px_rgba(11,31,58,0.35)]">
            <div className="lg:col-span-2 flex justify-center">
              <img src={SUBSCRIBE_BADGE} alt="Subscribe for daily devotionals" className="w-52 h-52 lg:w-64 lg:h-64 object-contain" />
            </div>
            <div className="lg:col-span-3">
              <div className="text-xs uppercase tracking-[0.3em] text-gold font-medium mb-3">Subscribe</div>
              <h2 className="font-display text-3xl lg:text-4xl text-navy-deep leading-tight">
                A devotional in your inbox. A breath of grace for your day.
              </h2>
              <p className="mt-4 text-navy/70">Join the Abide Daily family. No spam — just Scripture and encouragement.</p>
              <form onSubmit={onSubscribe} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="home-subscribe-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="home-subscribe-email"
                  className="flex-1 px-5 py-3.5 rounded-full border border-cream-200 bg-cream-50 focus-gold focus:outline-none text-navy-deep"
                />
                <button type="submit" data-testid="home-subscribe-submit" className="btn-primary px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2">
                  <Mail size={16} /> Subscribe
                </button>
              </form>
              {subStatus && (
                <p data-testid="home-subscribe-status" className="mt-3 text-sm text-gold font-medium">{subStatus}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <BottomNav exclude={["/"]} />
    </div>
  );
}
